import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Handle the password reset token from URL hash
    const handlePasswordReset = async () => {
      const hashFragment = window.location.hash;
      
      // Check if this is a password reset callback
      if (hashFragment.includes('type=recovery')) {
        // Supabase automatically handles the session from the URL
        // Just need to verify we have a valid session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setError('Invalid or expired reset link. Please request a new password reset.');
        }
      }
    };
    
    handlePasswordReset();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setSuccess(true);
      
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Password updated successfully! Please log in with your new password.' }
        });
      }, 2000);

    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to reset password. The link may have expired. Please request a new password reset.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-beige-50 to-rose-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-soft text-center"
        >
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-semibold text-charcoal-900 mb-2">Password Updated!</h2>
          <p className="text-charcoal-600 mb-4">
            Your password has been successfully updated. Redirecting you to login...
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-beige-50 to-rose-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="mt-6 text-3xl font-playfair font-semibold text-charcoal-900">
            Create New Password
          </h2>
          <p className="mt-2 text-sm text-charcoal-600">
            Enter your new password below
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-8 space-y-6 bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-soft"
          onSubmit={handleSubmit}
        >
          {error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-charcoal-700 mb-2">
                New Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ borderColor: '#f0e0dc' }}
                className="appearance-none relative block w-full px-4 py-3 border placeholder-charcoal-400 text-charcoal-900 rounded-2xl focus:outline-none focus:ring-2 focus:z-10 sm:text-sm transition-all duration-200"
                onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
                placeholder="Enter new password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-charcoal-700 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{ borderColor: '#f0e0dc' }}
                className="appearance-none relative block w-full px-4 py-3 border placeholder-charcoal-400 text-charcoal-900 rounded-2xl focus:outline-none focus:ring-2 focus:z-10 sm:text-sm transition-all duration-200"
                onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
                placeholder="Confirm new password"
              />
            </div>
          </div>

          <div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isLoading}
              style={{ backgroundColor: '#d9a79c' }}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-2xl text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-soft hover:shadow-soft-lg"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating Password...
                </div>
              ) : (
                'Update Password'
              )}
            </motion.button>
          </div>
        </motion.form>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-charcoal-500 hover:text-charcoal-700 transition-colors duration-200"
          >
            ← Back to Login
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
